import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { adminProductsList } from "@motolynk/api-client";
import { useTranslation } from "@motolynk/i18n";
import { Button, Combobox, Icon } from "@motolynk/ui";

export interface PickedProduct {
  uid: string;
  title: string;
}

/** Choose several products in order (e.g. "Products in this article"): search, add, remove. */
export function ProductPicker({
  id,
  value,
  onChange,
  max = 12,
}: {
  id?: string;
  value: PickedProduct[];
  onChange: (value: PickedProduct[]) => void;
  max?: number;
}) {
  const { t } = useTranslation("products");
  const [text, setText] = useState("");
  const query = { page_size: 20, ...(text ? { search: text } : {}) };
  const results = useQuery({
    queryKey: ["admin", "products", "picker", query],
    queryFn: async () => (await adminProductsList(query)).data,
  });
  const chosen = new Set(value.map((item) => item.uid));
  const options = (results.data?.results ?? [])
    .filter((product) => !chosen.has(product.uid))
    .map((product) => ({
      value: product.uid,
      label: product.title,
      description: product.vendor.is_platform ? t("list.company") : product.vendor.store_name,
    }));
  return (
    <div className="space-y-2">
      {value.length < max && (
        <Combobox
          id={id}
          value={null}
          options={options}
          onSearch={setText}
          onChange={(_, option) => {
            if (option) onChange([...value, { uid: option.value, title: option.label }]);
          }}
          loading={results.isFetching && !results.data}
          loadingLabel={t("picker.loading")}
          placeholder={t("picker.placeholder")}
          emptyLabel={t("picker.empty")}
          clearLabel={t("picker.clear")}
        />
      )}
      {value.length > 0 && (
        <ol className="divide-y divide-border rounded-card border border-border">
          {value.map((item) => (
            <li
              key={item.uid}
              className="flex items-center justify-between gap-2 px-3 py-2 text-sm"
            >
              <span className="min-w-0 truncate">{item.title}</span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={t("picker.remove", { title: item.title })}
                onClick={() => onChange(value.filter((other) => other.uid !== item.uid))}
              >
                <Icon name="close" size={16} />
              </Button>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
